<template>
  <transition name="incoming-slide">
    <div v-if="value" class="incoming-call-alert">
      <v-card class="incoming-card" elevation="14">
        <div class="incoming-header">
          <div class="pulse-dot" aria-hidden="true"></div>
          <div class="incoming-title">
            Appel entrant
            <span class="incoming-subtitle">Nouvelle demande</span>
          </div>
          <v-spacer />
          <v-btn icon small class="close-btn" @click="dismiss">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="incoming-body">
          <v-avatar class="caller-avatar" size="52" color="indigo">
            <span class="avatar-text">{{ callerInitials }}</span>
          </v-avatar>
          <div class="caller-meta">
            <div class="caller-name">{{ displayName }}</div>
            <div class="caller-contact">{{ displayContact }}</div>
          </div>
        </div>

        <div class="incoming-actions">
          <v-btn
            v-if="acceptButton"
            class="action-btn accept"
            color="success"
            depressed            
            @click="onAccept"
          >
            <v-icon left>mdi-phone</v-icon>
            Accepter
          </v-btn>
          <v-btn
            v-if="declineButton"
            class="action-btn decline"
            color="error"
            outlined            
            @click="onDecline"
          >
            <v-icon left>mdi-phone-hangup</v-icon>
            Refuser
          </v-btn>
          <v-btn
            v-if="caller && caller.id"
            class="action-btn details"
            color="primary"            
            :disabled="!caller || !caller.id"
            @click="openCustomerDetails"
          >
            <v-icon left>mdi-account-details</v-icon>
            Fiche collaborateur
          </v-btn>
        </div>
      </v-card>
    </div>
  </transition>
</template>

<script>
export default {
  name: "AIncomingCallAlert",
  props: {
    value: {
      type: Boolean,
      default: false
    },
    acceptButton: {
      type: Boolean,
      default: false
    },
    declineButton: {
      type: Boolean,
      default: false
    },
    caller: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      autoDismissTimer: null,
      autoDismissMs: 15000
    };
  },
  computed: {
    displayName() {
      const first = this.caller.firstname || "";
      const last = this.caller.lastname || "";
      const full = `${first} ${last}`.trim();
      return full || this.caller.name || "Client inconnu";
    },
    displayContact() {
      return this.caller.phone || this.caller.mail || "Coordonnees indisponibles";
    },
    callerInitials() {
      const first = (this.caller.firstname || "").trim();
      const last = (this.caller.lastname || "").trim();
      let initials = "";
      if (first) initials += first[0];
      if (last) initials += last[0];
      if (!initials && this.caller.name) {
        const parts = this.caller.name.trim().split(/\s+/);
        initials = parts.slice(0, 2).map(part => part[0]).join("");
      }
      return (initials || "?").toUpperCase();
    }
  },
  methods: {
    startAutoDismissTimer() {
      this.clearAutoDismissTimer();
      this.autoDismissTimer = setTimeout(() => {
        this.autoDismissTimer = null;
        this.dismiss();
      }, this.autoDismissMs);
    },
    clearAutoDismissTimer() {
      if (this.autoDismissTimer) {
        clearTimeout(this.autoDismissTimer);
        this.autoDismissTimer = null;
      }
    },
    dismiss() {
      this.clearAutoDismissTimer();
      this.$emit("input", false);
    },
    onAccept() {
      this.$emit("accept", this.caller);
      this.dismiss();
    },
    onDecline() {
      this.$emit("decline", this.caller);
      this.dismiss();
    },
    openCustomerDetails() {
      if (!this.caller || !this.caller.id) return;
      this.$router.push({
        name: "Profile",
        params: {
          id: this.caller.id,
          tab: 'tab-1'
        }
      });
      this.dismiss();
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(isVisible) {
        if (isVisible) {
          this.startAutoDismissTimer();
        } else {
          this.clearAutoDismissTimer();
        }
      }
    }
  },
  beforeDestroy() {
    this.clearAutoDismissTimer();
  }
};
</script>

<style scoped>
.incoming-call-alert {
  position: fixed;
  top: 84px;
  right: 16px;
  z-index: 9999;
  width: min(360px, calc(100vw - 32px));
}

.incoming-card {
  position: relative;
  overflow: hidden;
  border-radius: 18px;
  background: linear-gradient(135deg, rgba(24, 28, 40, 0.78), rgba(34, 39, 55, 0.78));
  color: #f7f7f7;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 20px 40px rgba(10, 12, 20, 0.35);
  padding: 16px 16px 12px;
  backdrop-filter: blur(18px) saturate(140%);
  -webkit-backdrop-filter: blur(18px) saturate(140%);
}

.incoming-card::before,
.incoming-card::after {
  content: "";
  position: absolute;
  inset: -40%;
  pointer-events: none;
  z-index: 0;
  background:
    radial-gradient(40% 40% at 30% 35%, rgba(94, 234, 212, 0.35), transparent 70%),
    radial-gradient(35% 35% at 70% 25%, rgba(129, 140, 248, 0.28), transparent 70%),
    radial-gradient(45% 45% at 60% 70%, rgba(248, 113, 113, 0.22), transparent 75%);
  filter: blur(22px);
  opacity: 0.8;
  transform: translate3d(-4%, 2%, 0) scale(1.05);
  animation: liquid-drift 12s ease-in-out infinite;
}

.incoming-card::after {
  background:
    radial-gradient(45% 45% at 20% 70%, rgba(56, 189, 248, 0.3), transparent 72%),
    radial-gradient(35% 35% at 80% 60%, rgba(244, 114, 182, 0.24), transparent 70%);
  opacity: 0.6;
  filter: blur(26px);
  transform: translate3d(6%, -4%, 0) scale(1.1);
  animation: liquid-drift-alt 16s ease-in-out infinite;
}

.incoming-card > * {
  position: relative;
  z-index: 1;
}

.incoming-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.incoming-title {
  font-weight: 700;
  font-size: 16px;
  display: flex;
  flex-direction: column;
  line-height: 1.1;
}

.incoming-subtitle {
  font-size: 12px;
  opacity: 0.7;
  font-weight: 500;
}

.close-btn {
  margin-left: auto;
  color: rgba(255, 255, 255, 0.7);
}

.pulse-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #4ade80;
  box-shadow: 0 0 0 0 rgba(74, 222, 128, 0.9);
  animation: pulse 1.6s infinite;
}

.incoming-body {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 12px;
  align-items: center;
  margin-bottom: 12px;
}

.caller-avatar {
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.15);
}

.avatar-text {
  font-size: 16px;
  font-weight: 600;
  color: #f8fafc;
  letter-spacing: 0.5px;
}

.caller-meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 13px;
}

.caller-name {
  font-weight: 600;
}

.caller-contact {
  opacity: 0.7;
}

.incoming-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.action-btn {
  border-radius: 14px;
  font-weight: 600;
  letter-spacing: 0.2px;
  text-transform: none;
}

.action-btn.accept {
  box-shadow: 0 10px 20px rgba(16, 185, 129, 0.25);
}

.action-btn.decline {
  border-color: rgba(248, 113, 113, 0.7);
}

.action-btn.details {
  grid-column: 1 / -1;
}

.incoming-slide-enter-active,
.incoming-slide-leave-active {
  transition: transform 0.35s ease, opacity 0.35s ease;
}

.incoming-slide-enter,
.incoming-slide-leave-to {
  transform: translateX(40px);
  opacity: 0;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(74, 222, 128, 0.8);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(74, 222, 128, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(74, 222, 128, 0);
  }
}

@keyframes liquid-drift {
  0% {
    transform: translate3d(-4%, 2%, 0) scale(1.05) rotate(0deg);
  }
  50% {
    transform: translate3d(3%, -2%, 0) scale(1.12) rotate(6deg);
  }
  100% {
    transform: translate3d(-4%, 2%, 0) scale(1.05) rotate(0deg);
  }
}

@keyframes liquid-drift-alt {
  0% {
    transform: translate3d(6%, -4%, 0) scale(1.1) rotate(0deg);
  }
  50% {
    transform: translate3d(-2%, 3%, 0) scale(1.18) rotate(-5deg);
  }
  100% {
    transform: translate3d(6%, -4%, 0) scale(1.1) rotate(0deg);
  }
}
</style>
